import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";
import {
  GuardrailCard,
  InfoBanner,
  StatusStrip,
  TryItPanel,
  createDraft,
} from "./-components";
import type { GuardrailDraft, GuardrailDrafts } from "./-components";
import type { GuardrailAction, GuardrailTestItem } from "@/lib/http/guardrails";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  guardrailCatalogQueryOptions,
  useTestGuardrails,
} from "@/lib/queries/guardrails";

export const Route = createFileRoute("/_app/guardrails/")({
  component: RouteComponent,
  loader({ context: { queryClient } }) {
    queryClient.ensureQueryData(guardrailCatalogQueryOptions());
  },
  staticData: {
    title: "Guardrails",
    isProblematicScrollPage: true,
    permissionGuards: [
      {
        resource: "guardrail_settings",
      },
    ],
  },
  pendingComponent: () => (
    <div className="flex h-full items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  ),
});

function RouteComponent() {
  const catalogQuery = useQuery(guardrailCatalogQueryOptions());
  const testMutation = useTestGuardrails();
  const [drafts, setDrafts] = React.useState<GuardrailDrafts>({});

  React.useLayoutEffect(() => {
    const catalog = catalogQuery.data;
    if (!catalog) return;
    setDrafts((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const item of catalog.guardrails) {
        if (!next[item.key]) {
          next[item.key] = createDraft(item);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [catalogQuery.data]);

  function patchDraft(key: string, patch: Partial<GuardrailDraft>) {
    setDrafts((prev) => {
      const current = prev[key];
      if (!current) return prev;
      return { ...prev, [key]: { ...current, ...patch } };
    });
  }

  if (catalogQuery.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (catalogQuery.isError) {
    return (
      <div className="w-full">
        <h1 className="mb-6 text-2xl font-semibold tracking-tight">
          Guardrails
        </h1>
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle>Could not load catalog</CardTitle>
            <CardDescription className="text-destructive">
              {catalogQuery.error.message ||
                "The guardrails catalog is unavailable."}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const catalog = catalogQuery.data;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold tracking-tight">Guardrails</h1>

      <InfoBanner />
      <div className="flex justify-end">
        <StatusStrip drafts={drafts} catalog={catalog.guardrails} />
      </div>

      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="space-y-3">
          {catalog.guardrails.map((item) => {
            const draft = drafts[item.key];
            if (!draft) return null;
            return (
              <GuardrailCard
                key={item.key}
                item={item}
                draft={draft}
                enums={catalog.enums}
                onToggle={(enabled) => patchDraft(item.key, { enabled })}
                onActionChange={(action: GuardrailAction) =>
                  patchDraft(item.key, { action })
                }
                onSettingChange={(settingKey, value) =>
                  patchDraft(item.key, {
                    settings: {
                      ...draft.settings,
                      [settingKey]: value,
                    },
                  })
                }
              />
            );
          })}
        </div>

        <TryItPanel
          catalog={catalog}
          drafts={drafts}
          isRunning={testMutation.isPending}
          result={testMutation.data}
          error={testMutation.error?.message ?? null}
          onRun={({ text, direction }) => {
            const guardrails: Array<GuardrailTestItem> = [];
            for (const item of catalog.guardrails) {
              const draft = drafts[item.key];
              if (!draft?.enabled) continue;
              const payload: GuardrailTestItem = {
                key: item.key,
                action: draft.action,
              };
              if (Object.keys(draft.settings).length > 0) {
                payload.settings = draft.settings;
              }
              guardrails.push(payload);
            }

            if (guardrails.length === 0) {
              toast.error("Enable at least one guardrail to run a test.");
              return;
            }

            testMutation.mutate({ text, direction, guardrails });
          }}
        />
      </div>
    </div>
  );
}
